import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';
import { CustomToggle } from '../components/CustomToggle';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';
import { SpinnerOverlay } from '../components/SpinnerOverlay';
import { TermsAndConditionsContent } from '../components/TermsAndConditionsContent';

/**
 * Terms and Conditions acceptance page for authenticated users
 * Used when a user has logged in but hasn't accepted T&C yet
 */
export const AcceptTermsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { user, isAuthenticated, getCurrentUser } = useAuthContext();

    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
    const [isSubmittingTerms, setIsSubmittingTerms] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(true);

    // Check if user is authenticated and has pending T&C status
    useEffect(() => {
        const checkUserStatus = async () => {
            try {
                if (!isAuthenticated) {
                    // If not authenticated, redirect to login
                    navigate('/auth/login', { replace: true });
                    return;
                }

                // Refresh user data to get latest status
                await getCurrentUser();

                // Wait a moment for state to update
                await new Promise(resolve => setTimeout(resolve, 100));

                // Check if user still has pending T&C status
                // If user is already active, redirect to home
                if (user?.status !== 'Pending Acceptance of Terms and Conditions') {
                    const redirectPath = sessionStorage.getItem('redirectAfterAcceptTerms') || '/';
                    sessionStorage.removeItem('redirectAfterAcceptTerms');
                    navigate(redirectPath, { replace: true });
                    return;
                }
            } catch (err) {
                debugError('Error checking user status:', err);
                setError('An error occurred while checking your account status. Please try again.');
            } finally {
                setIsChecking(false);
            }
        };

        checkUserStatus();
    }, [isAuthenticated, user, navigate, getCurrentUser]);

    const handleAcceptTerms = async () => {
        if (!hasAcceptedTerms) {
            return;
        }

        setIsSubmittingTerms(true);
        setError(null);

        try {
            const response = await authApiService.acceptTermsAndConditionsAuthenticated();

            if (response.data.success) {
                // Refresh user data to get updated status
                await getCurrentUser();

                // Get redirect path
                const redirectPath = sessionStorage.getItem('redirectAfterAcceptTerms') || '/';
                sessionStorage.removeItem('redirectAfterAcceptTerms');

                // Navigate to the intended destination
                navigate(redirectPath, { replace: true });
            } else {
                setError(response.data.message || 'Failed to accept terms and conditions. Please try again.');
            }
        } catch (err: any) {
            debugError('Failed to accept terms and conditions:', err);
            setError(err?.message || 'Failed to accept terms and conditions. Please try again.');
        } finally {
            setIsSubmittingTerms(false);
        }
    };

    if (isChecking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: theme.colors.background,
            }}>
                <SpinnerOverlay isVisible={true} message="Checking account status..." />
            </div>
        );
    }

    if (!isAuthenticated || user?.status !== 'Pending Acceptance of Terms and Conditions') {
        // This should not happen due to the useEffect, but handle it gracefully
        return null;
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.colors.background,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing.lg,
        }}>
            <div style={{
                maxWidth: '800px',
                width: '100%',
            }}>
                <div style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.lg,
                    padding: theme.spacing.xl,
                    border: `1px solid ${theme.colors.border.default}`,
                    boxShadow: theme.shadows.lg,
                }}>
                    <h1 style={{
                        ...themedStyles.text.heading,
                        fontSize: theme.typography.fontSize.xxl,
                        marginBottom: theme.spacing.md,
                        textAlign: 'center',
                    }}>
                        Accept Terms and Conditions
                    </h1>

                    <p style={{
                        ...themedStyles.text.body,
                        marginBottom: theme.spacing.lg,
                        textAlign: 'center',
                        color: theme.colors.text.secondary,
                    }}>
                        Before you can continue, you must accept the Terms and Conditions.
                    </p>

                    {error && (
                        <div style={{
                            backgroundColor: '#fee',
                            border: `1px solid ${theme.colors.status.error}`,
                            borderRadius: theme.borderRadius.md,
                            padding: theme.spacing.md,
                            marginBottom: theme.spacing.lg,
                            display: 'flex',
                            gap: theme.spacing.sm,
                            alignItems: 'flex-start',
                        }}>
                            <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                            <div>
                                <strong>Error</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                    {error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Terms and Conditions Content - Same as SignupPage */}
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius.md,
                        padding: theme.spacing.xl,
                        marginBottom: theme.spacing.lg,
                        border: `1px solid ${theme.colors.border.default}`,
                        maxHeight: '60vh',
                        overflow: 'auto',
                    }}>
                        <div
                            style={{
                                fontSize: theme.typography.fontSize.sm,
                                lineHeight: 1.8,
                                color: theme.colors.text.primary,
                            }}
                        >
                            <TermsAndConditionsContent />
                        </div>
                    </div>

                    {/* Accept Toggle and Button */}
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius.md,
                        padding: theme.spacing.lg,
                        border: `1px solid ${theme.colors.border.default}`,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing.md,
                    }}>
                        <CustomToggle
                            checked={hasAcceptedTerms}
                            onChange={(checked) => setHasAcceptedTerms(checked)}
                            disabled={isSubmittingTerms}
                            label="I have read and agree to the Terms and Conditions and Privacy Policy. *"
                            labelStyle={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.primary,
                            }}
                        />

                        <button
                            type="button"
                            onClick={handleAcceptTerms}
                            disabled={!hasAcceptedTerms || isSubmittingTerms}
                            style={{
                                ...themedStyles.button.primary,
                                flexShrink: 0,
                                opacity: (!hasAcceptedTerms || isSubmittingTerms) ? 0.6 : 1,
                                cursor: (!hasAcceptedTerms || isSubmittingTerms) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isSubmittingTerms ? 'Activating Account...' : 'Accept and Continue'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
